import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Loader from '@/components/Loader'
import { useLazyGetDriverProfileQuery, useLogoutMutation } from '@/services/Auth/AuthService'
import { useAppDispatch } from '@/store'
import { commitDriverProfile } from '@/store/reducers/DriverSlice'
import {
  AUTH_STORAGE_KEYS,
  clearAuthStorage,
  get,
  setAuthStorage,
} from '@/utils/storage/adapters/localStorageAdapter'

interface LoginPayload {
  token: string
  tokenExpiresAt: string
  refreshToken: string
  refreshTokenExpiresAt: string
}

interface AuthContextType {
  isLoggedIn: boolean
  isLoading: boolean
  authorize: () => Promise<void>
  login: (payload: LoginPayload) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface JWTProviderProps {
  children: ReactNode
}

const isDateExpired = (value?: string | null): boolean => {
  if (!value) return true

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return true

  return Date.now() >= date.getTime()
}

export const JWTProvider = ({ children }: JWTProviderProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [getDriverProfile] = useLazyGetDriverProfileQuery()
  const [triggerLogout] = useLogoutMutation()

  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isLoading, setIsLoading] = useState(() => {
    const token = get(AUTH_STORAGE_KEYS.token)
    const refreshToken = get(AUTH_STORAGE_KEYS.refreshToken)
    return Boolean(token && refreshToken)
  })

  const applyProfile = useCallback(async () => {
    const profile = await getDriverProfile().unwrap()
    dispatch(commitDriverProfile(profile))
  }, [dispatch, getDriverProfile])

  const authorize = useCallback(async () => {
    const token = get(AUTH_STORAGE_KEYS.token)
    const refreshToken = get(AUTH_STORAGE_KEYS.refreshToken)
    const refreshTokenExpiresAt = get(AUTH_STORAGE_KEYS.refreshTokenExpiresAt)

    if (!token || !refreshToken) {
      setIsLoggedIn(false)
      setIsLoading(false)
      return
    }

    if (isDateExpired(refreshTokenExpiresAt)) {
      clearAuthStorage()
      dispatch(commitDriverProfile({ fullName: '', phoneNumber: '' }))
      setIsLoggedIn(false)
      setIsLoading(false)
      return
    }

    try {
      await applyProfile()
      setIsLoggedIn(true)
    } catch (error) {
      window.console.error(error, 'authorize-error')
      clearAuthStorage()
      dispatch(commitDriverProfile({ fullName: '', phoneNumber: '' }))
      setIsLoggedIn(false)
    } finally {
      setIsLoading(false)
    }
  }, [applyProfile, dispatch])

  useEffect(() => {
    authorize()
  }, [authorize])

  const login = useCallback(
    async (payload: LoginPayload) => {
      setAuthStorage(payload)
      try {
        await applyProfile()
        setIsLoggedIn(true)
        navigate('/home')
      } catch (error) {
        clearAuthStorage()
        dispatch(commitDriverProfile({ fullName: '', phoneNumber: '' }))
        setIsLoggedIn(false)
        throw error
      }
    },
    [applyProfile, dispatch, navigate]
  )

  const logout = useCallback(async () => {
    try {
      await triggerLogout().unwrap()
    } catch (error) {
      window.console.error(error, 'logout-error')
    }
    clearAuthStorage()
    dispatch(commitDriverProfile({ fullName: '', phoneNumber: '' }))
    setIsLoggedIn(false)
    navigate('/login', { replace: true })
  }, [dispatch, navigate, triggerLogout])

  const value = useMemo(
    () => ({
      isLoggedIn,
      isLoading,
      authorize,
      login,
      logout,
    }),
    [isLoggedIn, isLoading, authorize, login, logout]
  )

  return (
    <AuthContext.Provider value={value}>{isLoading ? <Loader /> : children}</AuthContext.Provider>
  )
}

export default AuthContext
